import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import RouteLine from '../components/RouteLine';

const crumbs = [
  { label: 'Home', link: '/' },
  { label: 'Services' },
];

const services = [
  { 
    icon: 'fas fa-ship', 
    title: 'Shipbuilding & Construction', 
    desc: 'Custom construction of various ship types adhering to international and provincial class standards. Deep expertise across bulk carriers, MPPs, oil tankers, and ferries.' 
  },
  { 
    icon: 'fas fa-anchor', 
    title: 'Fleet Expansion & Enhancement', 
    desc: 'Design and building of highly optimized new vessels to enhance operations. Current active builds include bulk carriers with CCS class (CSR) and modern container ships.' 
  },
  { 
    icon: 'fas fa-tools', 
    title: 'Maintenance & Refurbishment', 
    desc: 'Comprehensive dry-dock maintenance and structural updates engineered to extend vessel lifespans and keep older fleets compliant with modern international maritime regulations.' 
  },
  { 
    icon: 'fas fa-users', 
    title: 'Crew Arrangement & Management', 
    desc: 'Expert assistance with maritime recruitment, vetting, and logistics, ensuring highly skilled, certified, and dependable personnel man your vessels.' 
  },
  { 
    icon: 'fas fa-graduation-cap', 
    title: 'Training Programs', 
    desc: 'Structured 5S industrial management and onboard workflows engineered to enhance active operational efficiency, deck productivity, and systemic safety.' 
  },
  { 
    icon: 'fas fa-clipboard-check', 
    title: 'Management Consultation', 
    desc: 'Strategic consultation powered by a strictly market-oriented and customer-first maritime philosophy to optimize vessel lifecycles and routes.' 
  },
];

const capabilities = [
  { icon: 'fas fa-oil-can', name: 'Oil & Chemical Tankers' },
  { icon: 'fas fa-cubes', name: 'Bulk Carriers' },
  { icon: 'fas fa-boxes', name: 'Multi-Purpose Ships (MPPs)' },
  { icon: 'fas fa-rv', name: 'Commercial Ferries' },
  { icon: 'fas fa-dharmachakra', name: 'CCS Class Compliance' },
  { icon: 'fas fa-shield-alt', name: 'CSR Specifications' },
];

export default function Services() {
  return (
    <>
      <Breadcrumb title="Our Services" crumbs={crumbs} />

      {/* Intro */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 50px' }}>
            <div className="eyebrow centered"><i className="fas fa-star"></i> Engineering Excellence</div>
            <h2 className="section-title centered">Maritime Expertise, Engineered for Reliability</h2>
            <p className="section-subtitle centered">
              At East Star Marine , Ltd. we bring decades of experience, a commitment to quality, 
              and a spirit of innovation to every project—ensuring safety and performance on every route.
            </p>
          </div>
          <div className="cards-grid">
            {services.map((s, i) => (
              <div key={i} className="service-card reveal">
                <span className="s-num">{String(i + 1).padStart(2, '0')}</span>
                <div className="s-icon"><i className={s.icon}></i></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link to="/contact" className="service-link">Inquire About This Service <i className="fas fa-arrow-right"></i></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section section-alt">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto' }}>
            <div className="eyebrow centered"><i className="fas fa-tasks"></i> Build Lifecycle</div>
            <h2 className="section-title centered">Four Steps From Blueprint to Blue Water</h2>
          </div>
          <div className="process-rail">
            <div className="process-connector"></div>
            <div className="process-grid">
              {[
                ['01', 'Technical Consultation', 'Reviewing required deadweight tonnage, class standards, and custom parameters.'],
                ['02', 'Design & Architecture', 'Mapping out structural lines, propulsion systems, and spatial arrangements.'],
                ['03', 'Yard Construction', 'Precision manufacturing, welding, and system integration within our high-capacity slips.'],
                ['04', 'Sea Trials & Delivery', 'Rigorous testing protocols and official class certification check-offs before deployment.'],
              ].map(([num, title, desc], i) => (
                <div key={i} className="process-step reveal">
                  <span className="p-num">{num}</span>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <RouteLine from="INITIAL BLUEPRINT" to="VESSEL DEPLOYED" light />
        </div>
      </section>

      {/* Cross-Link Banner to Products */}
      <div className="stats-band" style={{ padding: '40px 0' }}>
        <div className="container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '24px' }}>Looking for Specific Vessel Classes?</h3>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>Explore our full fleet matrix including Oil Tankers, Bulk Carriers, and Multi-Purpose Container Ships.</p>
          <Link to="/products" className="btn" style={{ background: '#fff', color: 'var(--primary)', fontWeight: 'bold', marginTop: '10px' }}>
            View Our Fleet Portfolio <i className="fas fa-ship"></i>
          </Link>
        </div>
      </div>

      {/* Capabilities / Formats */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '620px', margin: '0 auto' }}>
            <div className="eyebrow centered"><i className="fas fa-anchor"></i> Specializations</div>
            <h2 className="section-title centered">Vessels Configured to Global Benchmarks</h2>
          </div>
          <div className="industries-grid">
            {capabilities.map((cap, i) => (
              <div key={i} className="industry-chip reveal">
                <i className={cap.icon}></i>
                <span>{cap.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <div className="container">
          <div style={{ background: 'rgba(232,80,28,0.1)', border: '1px solid rgba(232,80,28,0.3)', borderRadius: '14px', padding: '44px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h3 style={{ color: '#fff', fontSize: '22px', marginBottom: '6px' }}>
                Begin Your Next Fleet Build Portfolio
              </h3>
              <p style={{ margin: 0 }}>Join us as we continue to set new benchmarks in the maritime industry, ensuring reliability, safety, and innovation.</p>
            </div>
            <Link to="/contact" className="btn btn-primary">Get in Touch <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>
    </>
  );
}